import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import deliveriesService from '../services/deliveriesService';
import projectsService from '../services/projectsService';
import { Check, X, Camera, FileText, Calendar, MapPin, User, Loader, CheckCircle, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_CHECKLIST = [
  'Móveis montados conforme projeto',
  'Eletrodomésticos instalados',
  'Verificação de funcionamento',
  'Limpeza do ambiente realizada',
  'Embalagens removidas',
  'Cliente aprovou o serviço'
];

const DeliveryModal = ({ projectId, projectName, clientName, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [deliveryData, setDeliveryData] = useState({
    delivery_date: new Date().toISOString().split('T')[0],
    client_name: clientName || '',
    location: '',
    observations: '',
    client_signature: '',
    signature_date: '',
    checklist: DEFAULT_CHECKLIST.map(name => ({ name, checked: true, observations: '' }))
  });

  // Buscar dados existentes da entrega
  const { data: existingDelivery } = useQuery({
    queryKey: ['delivery', projectId],
    queryFn: () => deliveriesService.getByProject(projectId),
    enabled: !!projectId
  });

  // Mutation para criar/atualizar entrega
  const deliveryMutation = useMutation({
    mutationFn: (data) => existingDelivery?.data?.data
      ? deliveriesService.update(projectId, data)
      : deliveriesService.create(projectId, data),
    onSuccess: () => {
      toast.success('Entrega registrada com sucesso!');
      queryClient.invalidateQueries(['project', projectId]);
      queryClient.invalidateQueries(['project-stages', projectId]);
      queryClient.invalidateQueries(['delivery', projectId]);
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao registrar entrega');
    }
  });

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const handleChecklistChange = (index, field, value) => {
    const newChecklist = [...deliveryData.checklist];
    newChecklist[index] = { ...newChecklist[index], [field]: value };
    setDeliveryData({ ...deliveryData, checklist: newChecklist });
  };

  const handleSubmit = () => {
    const allChecked = deliveryData.checklist.every(item => item.checked);
    if (!allChecked) {
      toast.error('Todos os itens do checklist devem estar marcados');
      return;
    }
    deliveryMutation.mutate(deliveryData);
  };

  const allChecked = deliveryData.checklist?.every(item => item.checked) || false;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-green-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Registrar Entrega</h2>
              <p className="text-sm text-gray-600">{projectName}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex border-b">
          <button
            onClick={() => setStep(1)}
            className={`flex-1 py-3 text-sm font-medium ${step === 1 ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-400'}`}
          >
            1. Checklist
          </button>
          <button
            onClick={() => setStep(2)}
            disabled={!allChecked}
            className={`flex-1 py-3 text-sm font-medium ${step === 2 ? 'border-b-2 border-green-500 text-green-600' : allChecked ? 'text-gray-600' : 'text-gray-300'}`}
          >
            2. Details
          </button>
          <button
            onClick={() => setStep(3)}
            disabled={step < 3}
            className={`flex-1 py-3 text-sm font-medium ${step === 3 ? 'border-b-2 border-green-500 text-green-600' : 'text-gray-300'}`}
          >
            3. Confirmar
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Checklist */}
          {step === 1 && (
            <div>
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Checklist de Entrega
              </h3>
              <div className="space-y-3">
                {deliveryData.checklist.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${item.checked ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <input
                      type="checkbox"
                      checked={item.checked}
                      onChange={(e) => handleChecklistChange(index, 'checked', e.target.checked)}
                      className="mt-1 w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />
                    <div className="flex-1">
                      <label className={`font-medium ${item.checked ? 'text-green-800' : 'text-gray-700'}`}>
                        {item.name}
                      </label>
                      <input
                        type="text"
                        placeholder="Observações (opcional)"
                        value={item.observations || ''}
                        onChange={(e) => handleChecklistChange(index, 'observations', e.target.value)}
                        className="mt-2 w-full p-2 text-sm border rounded"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p>Marque todos os itens realizados antes de prosseguir.</p>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Dados da Entrega
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data da Entrega *
                  </label>
                  <input
                    type="date"
                    value={deliveryData.delivery_date}
                    onChange={(e) => setDeliveryData({ ...deliveryData, delivery_date: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Cliente *
                  </label>
                  <input
                    type="text"
                    value={deliveryData.client_name}
                    onChange={(e) => setDeliveryData({ ...deliveryData, client_name: e.target.value })}
                    className="w-full p-2 border rounded-lg"
                    placeholder="Quem recebeu"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Local da Entrega
                </label>
                <input
                  type="text"
                  value={deliveryData.location}
                  onChange={(e) => setDeliveryData({ ...deliveryData, location: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Endereço ou descrição do local"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Observações
                </label>
                <textarea
                  value={deliveryData.observations}
                  onChange={(e) => setDeliveryData({ ...deliveryData, observations: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  rows={3}
                  placeholder="Observações gerais da entrega..."
                />
              </div>

              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-yellow-800 mb-2">Assinatura Digital</h4>
                <p className="text-sm text-yellow-700">
                  Para confirmar a entrega, o cliente deve assinar no campo abaixo.
                  Você pode usar um tablet ou dispositivo com tela sensível ao toque.
                </p>
                <div className="mt-3 border-2 border-dashed border-yellow-300 rounded-lg h-32 flex items-center justify-center bg-white">
                  <p className="text-sm text-gray-400">Área de assinatura (implementar canvas)</p>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800 mb-4">Confirmar Entrega</h3>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Data:</span>
                  <span className="font-medium">{formatDate(deliveryData.delivery_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Cliente:</span>
                  <span className="font-medium">{deliveryData.client_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Local:</span>
                  <span className="font-medium">{deliveryData.location || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Itens verificados:</span>
                  <span className="font-medium text-green-600">
                    {deliveryData.checklist.filter(i => i.checked).length}/{deliveryData.checklist.length}
                  </span>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg text-green-800 text-sm">
                <p>Ao confirmar, o projeto será marcado como <strong>ENTREGUE</strong> e não poderá voltar para etapas anteriores.</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            {step > 1 ? 'Voltar' : 'Cancelar'}
          </button>

          {step < 3 ? (
            <button
              onClick={() => {
                if (step === 1 && !allChecked) {
                  toast.error('Marque todos os itens do checklist');
                  return;
                }
                setStep(step + 1);
              }}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Próximo
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={deliveryMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {deliveryMutation.isPending ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              {deliveryMutation.isPending ? 'Registrando...' : 'Confirmar Entrega'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryModal;